import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCcw, AlertTriangle, Check } from "lucide-react";

/**
 * Public restore page (Iter-193) — hit via the restore link the owner copied
 * from the delete cool-down banner. Restores the company by presenting the
 * token to the server. No login required.
 */
export default function RestoreCompany() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orgId = params.get("org");
  const token = params.get("token");
  const [state, setState] = useState({ status: "idle", org_name: "", error: "" });

  const submit = async () => {
    if (!orgId || !token) { setState({ status: "error", error: "Missing org or token in the URL" }); return; }
    setState({ status: "busy", org_name: "", error: "" });
    try {
      const r = await api.post("/organization/restore", { org_id: orgId, token });
      setState({ status: "ok", org_name: r.data.org_name || "" });
    } catch (e) {
      setState({ status: "error", error: e?.response?.data?.detail || "Restore failed" });
    }
  };

  useEffect(() => { submit(); /* auto-run on landing */ // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6">
      <Card className="rounded-sm p-8 max-w-md w-full space-y-4" data-testid="restore-company-page">
        <div className="flex items-center gap-2 text-orange-600 text-sm font-mono uppercase tracking-widest">
          <RefreshCcw className="w-4 h-4" /> Restore Company
        </div>
        {state.status === "busy" && (
          <div className="text-sm text-muted-foreground">Restoring your company…</div>
        )}
        {state.status === "ok" && (
          <>
            <div className="flex items-center gap-2 text-emerald-700 text-lg font-semibold">
              <Check className="w-5 h-5" /> Restored
            </div>
            <div className="text-sm text-muted-foreground">
              <b>{state.org_name}</b> is live again. Sign in with your usual credentials.
            </div>
            <Button onClick={() => navigate("/login")} className="rounded-sm w-full" data-testid="restore-go-login">
              Go to sign-in
            </Button>
          </>
        )}
        {state.status === "error" && (
          <>
            <div className="flex items-center gap-2 text-red-700 text-lg font-semibold">
              <AlertTriangle className="w-5 h-5" /> Couldn't restore
            </div>
            <div className="text-sm text-muted-foreground">{state.error}</div>
            <Button onClick={submit} className="rounded-sm w-full" variant="outline" data-testid="restore-retry">
              Try again
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
